import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../../contexts";
import { useEmployerRoute } from "../../../../../../hooks";
import useRouteParam from "../../../../../../hooks/useRouteParam";
import { Group } from "../../../../../../@types/group.types";
import { Validation } from "../../../../../../@types/validation.types";
import { SnackbarProps } from "../../../../../../@types/snackbarAPI.types";
import { api } from "../../../../../../utils/api";
import { trackPromise } from "react-promise-tracker";
import JSONToExcel from "../../../../../../utils/commonFunctions/JSONToExcel";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../../constants/caption/dashboardHeader";
import SnackbarAPICall from "../../../../../shared/snackbar/SnackbarAPICall";
import DynamicReportEditableForm from "../../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { Member } from "../../../../../../@types/member.types";

const chipMessage = "This report will pull all the monthly employee of Change in Premium in a Particular Veb Plan.";

const VebMonthlyPremiumChangeReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, Select a Group to generate the report!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleEnableCheckbox = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, checked } = event.target;
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: checked }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Veb Monthly Premium Change Report Generation");

					const payload = {
						group_number: reportFieldDetails.group_number
					};

					const response: Member = await trackPromise(api.reports.vebPremiumChangeReport("monthly", payload));

					if (response) {
						// console.log("Response", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "Veb Monthly Premium Change Report";

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message:
										"Veb Monthly Premium Change Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				alert("Veb Monthly Premium Change Report Generation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails, user, snackbarAPICallProps]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.veb_monthly_premium_change_report);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Veb Monthly Premium Change Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		handleEnableCheckbox,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				handleClearDateField={() => null}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default VebMonthlyPremiumChangeReport;
